/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Badge, Tag, Empty, Tooltip, Skeleton } from 'antd';
import ResponsiveBreadcrumb from '../../components/breadcrumb/ResponsiveBreadcrumb';
import {
   BookOutlined, CheckCircleOutlined, ClockCircleOutlined, RollbackOutlined, RightOutlined
} from '@ant-design/icons';
import axios from 'axios';
import Utils from '../../misc/Utils';
import Enums from '../../misc/Enums';
import '../common/BraineeModule.css';
import { getCourses } from '../../services/MicrocredentialsCourseTopicService';
import { getMicrocredentialCourseId, getMicrocredentialCourseName, isMicrocredentialHomework } from './microcredentialUtils';

export function StudentHomeworkSubjects(props) {
   const navigate = useNavigate();
   const user_role = Utils.getCurrentUserInfo(Enums.UserInfo.role);
   const isMicrocredentials = props.isMicrocredentials || isMicrocredentialHomework(props.type);
   const workType = isMicrocredentials ? 'microcredential' : 'homework';
   const rootLabel = isMicrocredentials ? 'Brainee Microcredentials' : 'Brainee LMS';
   const pageTitle = isMicrocredentials ? 'Microcredential Homework Courses' : 'Homework Subjects';
   const heroKicker = isMicrocredentials ? 'Brainee Microcredentials' : 'Homework';
   const browseTitle = isMicrocredentials ? 'Course Groups' : 'Subject Groups';
   const listRoute = isMicrocredentials ? '/studentmicrocredentials/homeworks' : '/studenthomeworks';

   const [subjectList, setSubjectList] = useState([]);
   const [user_id] = useState(Utils.getCurrentUserInfo(Enums.UserInfo.id));
   const [loading, setLoading] = useState(false);

   useEffect(() => {
      Utils.verifyJwt();
      getSubjects(user_id);
   }, [isMicrocredentials, user_id, workType]);

   const getSubjects = async (userid) => {
      setLoading(true);

      try {
         if (isMicrocredentials) {
            const [courses, countsResponse] = await Promise.all([
               getCourses(),
               axios.get(`${process.env.REACT_APP_API_HOMEWORK}/studentsubjectgroup?id=${userid}&t=${workType}`),
            ]);

            const countsByCourseId = new Map();
            const countItems = Array.isArray(countsResponse?.data) ? countsResponse.data : [];
            countItems.forEach((item) => {
               countsByCourseId.set(String(item.value), item);
            });

            const mappedCourses = (Array.isArray(courses) ? courses : []).map((course) => {
               const courseId = getMicrocredentialCourseId(course);
               const counters = countsByCourseId.get(String(courseId)) || {};
               return {
                  value: courseId,
                  name: getMicrocredentialCourseName(course),
                  submitted: counters.submitted ?? 0,
                  not_submitted: counters.not_submitted ?? 0,
                  returned: counters.returned ?? 0,
               };
            }).filter((course) =>
               course.value !== null
               && course.name
               && (Number(course.submitted || 0) + Number(course.not_submitted || 0) + Number(course.returned || 0)) > 0
            );

            setSubjectList(mappedCourses);
         } else {
            const response = await axios.get(`${process.env.REACT_APP_API_HOMEWORK}/studentsubjectgroup?id=${userid}&t=${workType}`);
            if (response.data) {
               const subjects = response.data.map((item) => ({
                  value: item.value,
                  name: item.name,
                  submitted: item.submitted,
                  not_submitted: item.not_submitted,
                  returned: item.returned,
               }));
               setSubjectList(subjects);
            }
         }
      } catch (error) {
         console.log(error.message);
         setSubjectList([]);
      } finally {
         setLoading(false);
      }
   };

   const itemSubjectClick = (subject, subjectname) => {
      navigate(`${listRoute}?s=${subject}&sn=${encodeURIComponent(subjectname)}`);
   };

   const totalNotSubmitted = subjectList.reduce((sum, item) => sum + Number(item.not_submitted || 0), 0);
   const totalSubmitted = subjectList.reduce((sum, item) => sum + Number(item.submitted || 0), 0);
   const totalReturned = subjectList.reduce((sum, item) => sum + Number(item.returned || 0), 0);

   const renderSubjectSkeletons = () => (
      <div className="brainee-module-skeleton-grid">
         {Array.from({ length: 6 }).map((_, index) => (
            <div className="brainee-module-card is-skeleton" key={`student-homework-subject-skeleton-${index}`}>
               <Skeleton.Avatar active shape="square" className="brainee-module-card-skeleton-icon" />
               <div className="brainee-module-card-copy">
                  <Skeleton.Input active size="small" className="brainee-module-card-skeleton-title" />
                  <div className="brainee-module-card-skeleton-badges">
                     <Skeleton.Button active size="small" className="brainee-module-card-skeleton-badge" />
                     <Skeleton.Button active size="small" className="brainee-module-card-skeleton-badge" />
                  </div>
               </div>
               <Skeleton.Button active size="small" shape="circle" className="brainee-module-card-skeleton-action" />
            </div>
         ))}
      </div>
   );

   return (
      <>
         <div className="dashboard-area-all brainee-module-page">
            <div className="dashboard-item brainee-module-shell">
               <div className="brainee-module-breadcrumb">
                  <ResponsiveBreadcrumb
                     items={[
                        { key: 'home', node: <Link to="/home">Home</Link> },
                        { key: 'lms', node: rootLabel },
                        { key: 'hw', node: isMicrocredentials ? 'Homeworks & Projects' : 'Homework & Project' },
                     ]}
                  />
               </div>

               <section className="brainee-module-hero">
                  <div>
                     <span className="brainee-module-kicker">{heroKicker}</span>
                     <h1>{pageTitle}</h1>
                     <p>{isMicrocredentials ? 'Open a course to view pending, submitted, and returned microcredential homeworks & projects in one place.' : 'Open a subject to view pending, submitted, and returned homework in one place.'}</p>
                     <div className="brainee-module-meta">
                        <span><ClockCircleOutlined /> {loading ? 'Loading' : totalNotSubmitted} not submitted</span>
                        <span><CheckCircleOutlined /> {loading ? 'Loading' : totalSubmitted} submitted</span>
                        <span><RollbackOutlined /> {loading ? 'Loading' : totalReturned} returned</span>
                     </div>
                  </div>
               </section>

               <section className="brainee-module-panel">
                  <div className="brainee-module-panel-header">
                     <div>
                        <span className="brainee-module-kicker">Browse</span>
                        <h2>{browseTitle}</h2>
                     </div>
                     <span className="brainee-module-count">{loading ? '...' : subjectList.length}</span>
                  </div>

                  {loading
                     ? renderSubjectSkeletons()
                     : subjectList.length > 0
                        ? <div className="brainee-module-grid">
                           {subjectList.map((subject) => (
                              <button className="brainee-module-card" key={subject.value} type="button" onClick={() => itemSubjectClick(subject.value, subject.name)}>
                                 <span className="brainee-module-card-icon"><BookOutlined /></span>
                                 <span className="brainee-module-card-copy">
                                    <Tooltip placement="top" title={subject.name}>
                                       <span className="brainee-module-card-title">{subject.name}</span>
                                    </Tooltip>
                                    <span className="brainee-module-card-badges">
                                       <Badge count={subject.not_submitted} style={{ backgroundColor: 'gold' }} overflowCount={999}>
                                          <Tag color="gold">Not Submitted</Tag>
                                       </Badge>
                                       <Badge count={subject.submitted} style={{ backgroundColor: 'green' }} overflowCount={999}>
                                          <Tag color="green">Submitted</Tag>
                                       </Badge>
                                       <Badge count={subject.returned} style={{ backgroundColor: 'red' }} overflowCount={999}>
                                          <Tag color="red">Returned</Tag>
                                       </Badge>
                                    </span>
                                 </span>
                                 <RightOutlined className="brainee-module-card-arrow" />
                              </button>
                           ))}
                        </div>
                        : <div className='brainee-module-state'>
                           <Empty image="../../images/brainee_mascot.png" description="No records found." />
                        </div>
                  }
               </section>
            </div>
         </div>
      </>
   );
}
